import React, {useRef} from 'react';
import {Box, Button, Drawer, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CollectionsIcon from '@mui/icons-material/Collections';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {useTranslation} from 'react-i18next';
import SparkleIcon from '../AiMagic/SparkleIcon';
import {AiEditJobState} from '../../../services/jobService';

const SheetPaper = {
    sx: {
        borderRadius: '18px 18px 0 0',
        background: 'linear-gradient(220deg, #051726 36%, #025989 100%)',
        padding: 2,
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        // A bottom Drawer is as tall as its content and CLIPS the overflow: with
        // the layer z-order/delete row added, the bottom of the sheet fell off a
        // 844px-tall phone and those controls were unreachable. Cap it and let
        // the sheet scroll.
        maxHeight: '85vh',
        overflowY: 'auto',
    },
};

const ActionRow = styled(Button)(({theme}) => ({
    width: '100%',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    color: 'white',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    borderRadius: '12px',
    border: '1px solid #ffffff40',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.25),
}));

interface ImageActionSheetProps {
    open: boolean;
    label: string;
    allowVideo: boolean;
    allowReposition: boolean;
    /** State of an AI edit job left running for this field, if any — shows a
     *  resume row so the user can get back to it. */
    aiJobStatus?: AiEditJobState;
    onClose: () => void;
    onFileSelected: (file: File) => void;
    onOpenGallery: () => void;
    onOpenAiMagic?: () => void;
    onReposition: () => void;
    /** Extra controls at the bottom — the user-layer z-order/delete cluster.
     *  See TextEditSheet.extraControls for why this is a node, not callbacks. */
    extraControls?: React.ReactNode;
}

/** Bottom sheet for an image field: device upload first (the client's own photo
 *  is the main use case), asset gallery second, reposition when relevant. */
const ImageActionSheet: React.FC<ImageActionSheetProps> = ({
                                                               open,
                                                               label,
                                                               allowVideo,
                                                               allowReposition,
                                                               aiJobStatus,
                                                               onClose,
                                                               onFileSelected,
                                                               onOpenGallery,
                                                               onOpenAiMagic,
                                                               onReposition,
                                                               extraControls,
                                                           }) => {
    const {t} = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Allow re-picking the same file next time the sheet opens.
        event.target.value = '';
        if (file) onFileSelected(file);
    };

    return (
        <Drawer anchor="bottom" open={open} onClose={onClose} PaperProps={SheetPaper}>
            <Box sx={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#ffffff50', margin: '0 auto 12px'}}/>
            <Typography sx={{color: 'white', fontWeight: 700, fontSize: '1.05rem', mb: 0.5}}>
                {label}
            </Typography>
            <Typography sx={{color: '#8fb0c8', fontSize: '0.85rem', mb: 2}}>
                {t('editor.fillMode.imageSheetHint')}
            </Typography>

            {aiJobStatus && aiJobStatus !== 'failed' && onOpenAiMagic && (
                <ActionRow
                    data-testid="fill-action-ai-magic"
                    onClick={onOpenAiMagic}
                    sx={{backgroundColor: '#C3F3FD26', borderColor: '#C3F3FD'}}
                >
                    <SparkleIcon/>
                    {t(aiJobStatus === 'completed' ? 'aiEdit.resumeReady' : 'aiEdit.resumeRunning')}
                </ActionRow>
            )}

            <ActionRow
                data-testid="fill-action-upload"
                onClick={() => fileInputRef.current?.click()}
                sx={{backgroundColor: '#00E162', border: 'none', '&:hover': {backgroundColor: '#3D9A4A'}}}
            >
                <AddPhotoAlternateIcon/>
                {t('editor.fillMode.uploadPhoto')}
            </ActionRow>

            <ActionRow data-testid="fill-action-gallery" onClick={onOpenGallery}>
                <CollectionsIcon/>
                {t('editor.fillMode.chooseFromGallery')}
            </ActionRow>

            {allowReposition && (
                <ActionRow data-testid="fill-action-reposition" onClick={onReposition}>
                    <OpenWithIcon/>
                    {t('editor.fillMode.reposition')}
                </ActionRow>
            )}

            {extraControls}

            <input
                type="file"
                ref={fileInputRef}
                style={{display: 'none'}}
                accept={allowVideo ? 'image/*,video/mp4,video/webm,video/quicktime' : 'image/*'}
                onChange={handleFileChange}
            />
        </Drawer>
    );
};

export default ImageActionSheet;
